import React, { useMemo, useState } from 'react';
import totalsForSelection, {
  materialsOfCable,
  hasCopperBreakdown
} from '../../helpers/consumptionTotals';
import { Form, Button, Table, Row, Col, Card, Alert, Badge } from 'react-bootstrap';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFlaskVial,
  faPen,
  faTrash,
  faBolt,
  faLayerGroup
} from '@fortawesome/free-solid-svg-icons';
import MaterialTotalsChart from '../../components/charts/materials-charts/MaterialTotalsChart';
import PhoenixBadge from '../../components/base/Badge';
import ProductColorDot from '../../components/common/ProductColorDot';
import {
  CABLE_COLOR_HEX,
  productColorBackground
} from '../../helpers/productColor';
import { CABLE_NAMES_NO_ROPE, CABLE_COLORS } from '../../data/cableProducts.js';
import { API } from '../../config/api';

const cableTypes = CABLE_NAMES_NO_ROPE;

const colorOptions = CABLE_COLORS;

// readable black/white text on a colour swatch
const textOn = colorName => {
  const hex = CABLE_COLOR_HEX[String(colorName || '').trim().toUpperCase()];
  if (!hex || hex.length < 7) return '#fff';
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return 0.299 * r + 0.587 * g + 0.114 * b > 150 ? '#111' : '#fff';
};

// coloured product-colour label (same look as the order table)
const ColorLabel = ({ color }) =>
  color ? (
    <span
      className="badge rounded-pill px-3 py-2 fw-semibold"
      style={{
        background: productColorBackground(color),
        color: textOn(color),
        border: '1px solid rgba(0,0,0,0.15)'
      }}
    >
      {String(color).toUpperCase()}
    </span>
  ) : (
    <span className="text-muted">—</span>
  );

const fmtKg = n => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

// colour a material chip by its type (no more flat grey for everything)
const materialBg = m => {
  const s = String(m || '').toUpperCase();
  if (s.includes('PVC')) return 'success';
  if (s.includes('POLIET') || s === 'PE' || s.includes('POLYET')) return 'info';
  if (s.includes('COPPER') || s.includes('CUPRU')) return 'warning';
  if (s.includes('BLR') || s.includes('STEEL') || s.includes('OTEL') || s.includes('OȚEL'))
    return 'secondary';
  if (s.includes('NATUR')) return 'warning';
  return 'primary';
};

const StandardConsumption = () => {
  const [formData, setFormData] = useState({
    cableName: '',
    numberOfConductors: '',
    section: '',
    length: '',
    color: '',
    hasMixedConductorSections: false,
    greenYellowSection: ''
  });

  const [simulationResult, setSimulationResult] = useState(null);
  // Which result cards are counted in the totals. Indices into
  // `simulationResult.result`, so a card can be taken out of the picture
  // without being taken out of the list — the cable is still there, it just
  // stops contributing.
  const [excluded, setExcluded] = useState(() => new Set());
  const [cables, setCables] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [error, setError] = useState('');

  const handleChange = e => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        hasMixedConductorSections: checked,
        greenYellowSection: checked ? prev.greenYellowSection : ''
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    setError('');
  };

  const resetForm = () => {
    setFormData({
      cableName: '',
      numberOfConductors: '',
      section: '',
      length: '',
      color: '',
      hasMixedConductorSections: false,
      greenYellowSection: ''
    });
    setEditingIndex(null);
    setError('');
  };

  const isCurrentFormValid =
    formData.cableName.trim() !== '' &&
    formData.numberOfConductors !== '' &&
    formData.section !== '' &&
    formData.length !== '' &&
    formData.color.trim() !== '' &&
    (!formData.hasMixedConductorSections || formData.greenYellowSection !== '');

  const handleAddCable = () => {
    if (!isCurrentFormValid) {
      setError('Please fill in all required fields before adding the cable.');
      return;
    }

    const newCable = {
      cableName: formData.cableName,
      numberOfConductors: Number(formData.numberOfConductors),
      section: Number(formData.section),
      greenYellowSection: formData.hasMixedConductorSections
        ? Number(formData.greenYellowSection)
        : null,
      hasMixedConductorSections: !!formData.hasMixedConductorSections,
      length: Number(formData.length),
      color: formData.color
    };

    if (editingIndex !== null) {
      const updated = [...cables];
      updated[editingIndex] = newCable;
      setCables(updated);
      resetForm();
      return;
    }

    const existingIndex = cables.findIndex(
      c =>
        c.cableName === newCable.cableName &&
        c.numberOfConductors === newCable.numberOfConductors &&
        c.section === newCable.section &&
        Number(c.greenYellowSection || 0) ===
          Number(newCable.greenYellowSection || 0) &&
        String(c.color || '')
          .trim()
          .toUpperCase() ===
          String(newCable.color || '')
            .trim()
            .toUpperCase()
    );

    if (existingIndex !== -1) {
      const updated = [...cables];
      updated[existingIndex].length += newCable.length;
      setCables(updated);
    } else {
      setCables(prev => [...prev, newCable]);
    }

    resetForm();
  };

  const handleEditCable = index => {
    const cableToEdit = cables[index];

    setFormData({
      cableName: cableToEdit.cableName || '',
      numberOfConductors: cableToEdit.numberOfConductors || '',
      section: cableToEdit.section || '',
      length: cableToEdit.length || '',
      color: cableToEdit.color || '',
      hasMixedConductorSections: !!cableToEdit.hasMixedConductorSections,
      greenYellowSection: cableToEdit.greenYellowSection || ''
    });

    setEditingIndex(index);
    setError('');
  };

  const handleRemoveCable = index => {
    if (window.confirm('Are you sure you want to remove this cable?')) {
      setCables(prev => prev.filter((_, i) => i !== index));

      if (editingIndex === index) {
        resetForm();
      }
    }
  };

  const handleSimulation = async () => {
    try {
      console.log('Cables before simulation:', cables);

      const response = await axios.post(
        `${API.BACKEND}/api/v1/simulation/products`,
        { cables }
      );

      setSimulationResult(response.data);
      // A fresh simulation is a fresh picture — nothing carried over from the
      // last one's deselections.
      setExcluded(new Set());
    } catch (error) {
      console.error('Simulation error:', error);
      alert('Failed to start simulation.');
    }
  };

  // Memoised for its identity, not its cost: `?? []` hands back a new array on
  // every render, and the three memos below would then recompute every time the
  // form is typed into.
  const results = useMemo(() => simulationResult?.result || [], [simulationResult]);

  const selected = useMemo(() => {
    const keep = new Set();
    results.forEach((_, i) => {
      if (!excluded.has(i)) keep.add(i);
    });
    return keep;
  }, [results, excluded]);

  // Re-added from the per-cable figures rather than re-asked of the server.
  // With nothing deselected these equal `simulationResult.totals` exactly —
  // same addends, same keys — so the charts do not jump when the page starts
  // doing its own arithmetic. See helpers/consumptionTotals.js.
  const live = useMemo(() => totalsForSelection(results, selected), [results, selected]);
  const canRebuildCopper = useMemo(() => hasCopperBreakdown(results), [results]);

  const toggleCable = index =>
    setExcluded(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });

  return (
    <div className="container mt-4">
      <div className="d-flex align-items-center gap-3 mb-4">
        <div
          className="d-flex align-items-center justify-content-center rounded-3"
          style={{ width: 48, height: 48, background: '#3874ff22', color: '#3874ff' }}
        >
          <FontAwesomeIcon icon={faFlaskVial} size="lg" />
        </div>
        <div>
          <h2 className="mb-0">Standard Consumption Simulation</h2>
          <span className="text-700 fs-9">
            Estimate copper + material consumption for a batch of cables from the
            standards.
          </span>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="mb-4 shadow-sm">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div className="fw-semibold">
            {editingIndex !== null
              ? `Edit Cable #${editingIndex + 1}`
              : 'Add Cable'}
          </div>

          <Form.Check
            type="switch"
            id="hasMixedConductorSections"
            name="hasMixedConductorSections"
            label="GY different section"
            checked={formData.hasMixedConductorSections}
            onChange={handleChange}
          />
        </Card.Header>

        <Card.Body>
          <Form>
            <Row className="mb-3">
              <Col md={4}>
                <Form.Label>Name *</Form.Label>
                <Form.Select
                  name="cableName"
                  value={formData.cableName}
                  onChange={handleChange}
                >
                  <option value="">Select</option>
                  {cableTypes.map(type => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={4}>
                <Form.Label>Conductors *</Form.Label>
                <Form.Control
                  type="number"
                  name="numberOfConductors"
                  value={formData.numberOfConductors}
                  onChange={handleChange}
                />
              </Col>

              <Col md={4}>
                <Form.Label>Main Section *</Form.Label>
                <Form.Control
                  type="number"
                  name="section"
                  value={formData.section}
                  onChange={handleChange}
                />
              </Col>
            </Row>

            {formData.hasMixedConductorSections && (
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Label className="text-success">
                    Green-Yellow Section *
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="greenYellowSection"
                    value={formData.greenYellowSection}
                    onChange={handleChange}
                  />
                </Col>
              </Row>
            )}

            <Row className="mb-3">
              <Col md={4}>
                <Form.Label>Length *</Form.Label>
                <Form.Control
                  type="number"
                  name="length"
                  value={formData.length}
                  onChange={handleChange}
                />
              </Col>

              <Col md={4}>
                <Form.Label>Color *</Form.Label>
                <Form.Select
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                >
                  <option value="">Select</option>
                  {colorOptions.map(color => (
                    <option key={color} value={color}>
                      {color}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>

            <div className="text-end">
              {editingIndex !== null && (
                <Button
                  variant="outline-secondary"
                  className="me-2"
                  onClick={resetForm}
                >
                  Cancel
                </Button>
              )}

              <Button variant="success" onClick={handleAddCable}>
                {editingIndex !== null ? 'Update Cable' : 'Add Cable'}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {cables.length > 0 && (
        <Card className="mt-4 shadow-sm">
          <Card.Header className="d-flex align-items-center justify-content-between">
            <h4 className="mb-0">
              <FontAwesomeIcon
                icon={faLayerGroup}
                className="me-2 text-primary"
              />
              Simulation list{' '}
              <span className="fw-normal text-700 fs-9">({cables.length})</span>
            </h4>
            <Button variant="primary" onClick={handleSimulation}>
              <FontAwesomeIcon icon={faBolt} className="me-2" />
              Create simulation
            </Button>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle fs-9">
                <thead className="text-body-tertiary">
                  <tr>
                    <th className="ps-4">#</th>
                    <th>Cable</th>
                    <th className="text-center">Conductors</th>
                    <th className="text-center">Section (mm²)</th>
                    <th>Color</th>
                    <th className="text-end">Length</th>
                    <th className="text-end pe-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {cables.map((cable, index) => (
                    <tr key={index}>
                      <td className="ps-4 text-700">{index + 1}</td>
                      <td>
                        <span className="fw-semibold d-inline-flex align-items-center gap-2">
                          <ProductColorDot colorName={cable.color} />
                          {cable.cableName}
                        </span>
                      </td>
                      <td className="text-center">
                        {cable.numberOfConductors}
                      </td>
                      <td className="text-center">
                        {cable.greenYellowSection
                          ? `${cable.section} + ${cable.greenYellowSection}`
                          : cable.section}
                      </td>
                      <td>
                        <ColorLabel color={cable.color} />
                      </td>
                      <td className="text-end fw-semibold">
                        {Number(cable.length).toLocaleString()} m
                      </td>
                      <td className="text-end pe-4 text-nowrap">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="me-2"
                          onClick={() => handleEditCable(index)}
                          title="Edit"
                        >
                          <FontAwesomeIcon icon={faPen} />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleRemoveCable(index)}
                          title="Delete"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}

      {simulationResult?.missing?.length > 0 && (
        <div className="mt-4 alert alert-warning">
          <h5>Missing Standards:</h5>
          <ul>
            {simulationResult.missing.map((item, index) => (
              <li key={index}>
                {item.cableName} - {item.numberOfConductors}x{item.section} mm²
                {item.greenYellowSection
                  ? ` + ${item.greenYellowSection}`
                  : ''}{' '}
                ({item.length} m)
                {item.color ? ` - ${item.color}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}

      {simulationResult?.result?.length > 0 && (
        <div className="mt-5">
          <h4 className="mb-3">
            <FontAwesomeIcon icon={faLayerGroup} className="me-2 text-primary" />
            Individual cable results
          </h4>
          <Row className="g-3">
            {simulationResult.result.map((cable, index) => {
              const accent =
                CABLE_COLOR_HEX[String(cable.color || '').toUpperCase()] ||
                '#3874ff';
              const isOut = excluded.has(index);
              const byMaterial = materialsOfCable(cable);
              const cableTotalKg =
                Number(cable.copperWeightTotalKg || 0) +
                byMaterial.reduce((s, m) => s + m.kg, 0);

              return (
                <Col md={6} lg={4} key={index}>
                  <Card
                    className="h-100 shadow-sm border-0"
                    style={{
                      borderLeft: `4px solid ${accent}`,
                      // Still legible, visibly not counted.
                      opacity: isOut ? 0.5 : 1
                    }}
                  >
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                        <h5 className="mb-0 d-inline-flex align-items-center gap-2">
                          <ProductColorDot colorName={cable.color} size={16} />
                          {cable.name} {cable.numberOfConductors}x
                          {cable.section}
                          {cable.greenYellowSection
                            ? ` + ${cable.greenYellowSection}`
                            : ''}{' '}
                          mm²
                        </h5>
                        <ColorLabel color={cable.color} />
                      </div>

                      {/* Deselecting takes this cable out of the totals and the
                          charts below without removing it from the batch, so
                          "what if we did not make this one" is one tap. */}
                      {!cable.error && (
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <Form.Check
                            type="switch"
                            id={`count-cable-${index}`}
                            className="fs-9 mb-0"
                            label={isOut ? 'Not counted' : 'Counted in totals'}
                            checked={!isOut}
                            onChange={() => toggleCable(index)}
                          />
                          <span className="fw-bold fs-9">
                            {fmtKg(cableTotalKg)}{' '}
                            <span className="fw-normal text-700">kg total</span>
                          </span>
                        </div>
                      )}

                      <div className="text-700 fs-9 mb-3">
                        Length: <b>{Number(cable.lengthMeters).toLocaleString()}</b> m
                      </div>

                      <div
                        className="d-flex align-items-center justify-content-between rounded-2 px-3 py-2 mb-3"
                        style={{ background: '#b5651d18' }}
                      >
                        <span className="fw-semibold" style={{ color: '#b5651d' }}>
                          <FontAwesomeIcon icon={faBolt} className="me-2" />
                          Copper
                        </span>
                        <span className="fw-bold" style={{ color: '#b5651d' }}>
                          {fmtKg(cable.copperWeightTotalKg)} kg
                        </span>
                      </div>

                      {/* Per COMPOUND, which is what gets ordered — an inner
                          and an outer sheath of the same PVC are two layers and
                          one material, and the card only ever showed the layers. */}
                      {byMaterial.length > 0 && (
                        <div className="mb-3">
                          <div className="fs-10 text-700 text-uppercase fw-bold mb-1">
                            Total per material
                          </div>
                          {byMaterial.map(m => (
                            <div
                              key={m.key}
                              className="d-flex justify-content-between align-items-center fs-9"
                            >
                              <PhoenixBadge
                                bg={materialBg(m.material)}
                                variant="phoenix"
                                className="fs-10 fw-normal"
                              >
                                {m.material} {m.type}
                              </PhoenixBadge>
                              <span className="fw-bold">
                                {fmtKg(m.kg)} kg
                                {m.layers.length > 1 && (
                                  <span className="fw-normal text-700 fs-10">
                                    {' '}
                                    ({m.layers.length} layers)
                                  </span>
                                )}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="fs-10 text-700 text-uppercase fw-bold mb-1">
                        By layer
                      </div>
                      <div className="d-flex flex-column gap-1">
                        {cable.layers.map((layer, idx) => (
                          <div
                            key={idx}
                            className="d-flex justify-content-between align-items-center fs-9"
                          >
                            <span className="text-700 text-capitalize">
                              {String(layer.layer || '').replace(/_/g, ' ')}
                              {layer.material || layer.type ? (
                                <PhoenixBadge
                                  bg={materialBg(layer.material)}
                                  variant="phoenix"
                                  className="ms-2 fs-10 fw-normal"
                                >
                                  {layer.material} {layer.type}
                                </PhoenixBadge>
                              ) : null}
                            </span>
                            <span className="fw-semibold">
                              {fmtKg(layer.quantityTotal)} kg
                            </span>
                          </div>
                        ))}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </div>
      )}

      {simulationResult?.totals && (
        <div className="mt-5">
          <h4 className="mb-3">
            <FontAwesomeIcon icon={faBolt} className="me-2 text-primary" />
            Total material consumption
          </h4>

          <Row className="g-3 mb-3">
            <Col sm={4}>
              <Card
                className="h-100 border-0 shadow-sm"
                style={{ borderLeft: '4px solid #b5651d' }}
              >
                <Card.Body className="d-flex align-items-center gap-3">
                  <FontAwesomeIcon
                    icon={faBolt}
                    style={{ color: '#b5651d' }}
                    size="lg"
                  />
                  <div>
                    <div className="fs-9 text-700">Total copper</div>
                    <div className="fs-6 fw-bold">
                      {fmtKg(live.copperKg)}{' '}
                      <span className="fs-9 fw-normal text-700">kg</span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col sm={4}>
              <Card
                className="h-100 border-0 shadow-sm"
                style={{ borderLeft: '4px solid #3874ff' }}
              >
                <Card.Body className="d-flex align-items-center gap-3">
                  <FontAwesomeIcon
                    icon={faLayerGroup}
                    className="text-primary"
                    size="lg"
                  />
                  <div>
                    <div className="fs-9 text-700">Cables counted</div>
                    <div className="fs-6 fw-bold">
                      {live.cables}
                      {excluded.size > 0 && (
                        <span className="fs-9 fw-normal text-700">
                          {' '}
                          of {results.filter(c => !c.error).length}
                        </span>
                      )}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col sm={4}>
              <Card
                className="h-100 border-0 shadow-sm"
                style={{ borderLeft: '4px solid #2f9e44' }}
              >
                <Card.Body className="d-flex align-items-center gap-3">
                  <FontAwesomeIcon
                    icon={faFlaskVial}
                    style={{ color: '#2f9e44' }}
                    size="lg"
                  />
                  <div>
                    <div className="fs-9 text-700">Total length</div>
                    <div className="fs-6 fw-bold">
                      {live.lengthM.toLocaleString()}{' '}
                      <span className="fs-9 fw-normal text-700">m</span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <div className="row">
            <div className="col-md-4 mb-3">
              <MaterialTotalsChart
                data={canRebuildCopper ? live.copper : simulationResult.totals.copper}
                title={
                  excluded.size > 0
                    ? `Copper — ${live.cables} cable(s)`
                    : 'Copper Consumption'
                }
                unit="kg"
              />
              {excluded.size > 0 && !canRebuildCopper && (
                <div className="fs-10 text-warning mt-1">
                  This result predates the per-cable copper breakdown, so the
                  copper chart is still the whole batch. Re-run the simulation to
                  split it by gauge.
                </div>
              )}
            </div>
            <div className="col-md-8 mb-3">
              <MaterialTotalsChart
                data={live.materials}
                title={
                  excluded.size > 0
                    ? `Materials — ${live.cables} cable(s)`
                    : 'Material Consumption (PVC, PE, etc)'
                }
                unit="kg"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StandardConsumption;
